/**
 * 各模块的家长预览（只看，不上报进度）：PDF 翻页、视频播放、写字列出字 / 拼音 / 组词、H5 显示信息。
 * 对应 iOS ActivityPreview（iOS 直接运行学习模块；网页版没有模块播放器，做简单的查看器）。在 modules.tsx 登记。
 */
import type { PDFDocumentProxy } from "pdfjs-dist";
import { ChevronLeft, ChevronRight, Gamepad2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { gameOf, type WebPackage } from "../../activities/games";
import { type FormField, parseFormSchema } from "../../activities/schema-form";
import { assetUrl, type Content, type JSONValue } from "../../core";
import { EmptyHint, FormCard, Pill, RowDivider, Spinner, ValueRow } from "../../ui";
import { bool, int, isObject, payloadOf, str } from "./activity";
import { HanziGlyph, useHanziData } from "./hanzi";
import { openPdf, pdfUrl, renderPage } from "./pdf";

// ---------- PDF ----------

export function PdfPreview({ content }: { content: Content }) {
  const p = payloadOf(content);
  const assetId = str(p.pdf_asset_id);
  const range = Array.isArray(p.page_range) ? p.page_range.map((x) => int(x)).filter((x): x is number => x != null) : [];
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(range.length === 2 ? Math.max(1, range[0]!) : 1);
  const [rendering, setRendering] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!assetId) {
      setError("这份内容没有 PDF 文件");
      return;
    }
    let alive = true;
    let opened: PDFDocumentProxy | null = null;
    pdfUrl(assetId)
      .then(openPdf)
      .then((d) => {
        opened = d;
        if (alive) {
          setDoc(d);
          setPage((x) => Math.min(Math.max(1, x), d.numPages));
        } else void d.loadingTask.destroy();
      })
      .catch((e: unknown) => alive && setError(e instanceof Error ? `PDF 打开失败：${e.message}` : "PDF 打开失败"));
    return () => {
      alive = false;
      if (opened) void opened.loadingTask.destroy();
    };
  }, [assetId]);

  // 宽度跟着弹窗变化
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.floor(el.clientWidth)));
    ro.observe(el);
    setWidth(Math.floor(el.clientWidth));
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!doc || !canvas.current || width <= 0) return;
    let cancel: (() => void) | null = null;
    let alive = true;
    setRendering(true);
    renderPage(doc, page, canvas.current, width, (c) => (cancel = c))
      .catch((e: unknown) => {
        if ((e as Error)?.name !== "RenderingCancelledException" && alive) setError("页面渲染失败");
      })
      .finally(() => alive && setRendering(false));
    return () => {
      alive = false;
      cancel?.();
    };
  }, [doc, page, width]);

  const n = doc?.numPages ?? 0;
  const rule = str(p.complete_when) === "min_minutes" ? `读满 ${int(p.min_minutes) ?? 5} 分钟算完成` : "读到最后一页算完成";
  return (
    <div className="learn-pdf">
      <div className="learn-preview-tags">
        {range.length === 2 && <Pill tint="var(--kp-course-tint)" ink="var(--kp-course-ink)">只读第 {range[0]}–{range[1]} 页</Pill>}
        <Pill>{rule}</Pill>
        {bool(p.allow_annotation) && <Pill>允许画笔批注</Pill>}
      </div>
      <div ref={box} className="learn-pdf-stage">
        {error ? <EmptyHint text={error} /> : !doc && <Spinner size={28} />}
        <canvas ref={canvas} hidden={!doc || !!error} data-testid="pdf-canvas" />
      </div>
      {doc && !error && (
        <div className="learn-pdf-pager">
          <button type="button" className="kp-icon-btn" aria-label="上一页" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <ChevronLeft size={20} strokeWidth={2.6} />
          </button>
          <span className="kp-num" data-testid="pdf-page">
            {page} / {n}
            {rendering && <Spinner size={12} />}
          </span>
          <button type="button" className="kp-icon-btn" aria-label="下一页" disabled={page >= n} onClick={() => setPage(page + 1)}>
            <ChevronRight size={20} strokeWidth={2.6} />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- 视频 ----------

function useAssetUrl(id: string | null): { url: string | null; error: string | null } {
  const [state, setState] = useState<{ url: string | null; error: string | null }>({ url: null, error: null });
  useEffect(() => {
    if (!id) return;
    let alive = true;
    assetUrl(id)
      .then((url) => alive && setState({ url, error: url ? null : "网页版不能打开内置资源" }))
      .catch((e: unknown) => alive && setState({ url: null, error: e instanceof Error ? e.message : String(e) }));
    return () => {
      alive = false;
    };
  }, [id]);
  return state;
}

export function VideoPreview({ content }: { content: Content }) {
  const p = payloadOf(content);
  const video = useAssetUrl(str(p.video_asset_id));
  const poster = useAssetUrl(str(p.poster_asset_id));
  const percent = Math.min(100, Math.max(1, int(p.complete_percent) ?? 90));
  return (
    <div className="learn-video">
      <div className="learn-preview-tags">
        <Pill>看到 {percent}% 算完成</Pill>
        <Pill>{bool(p.allow_seek) === false ? "不能拖动进度条" : "允许拖动进度条"}</Pill>
      </div>
      {video.error ? (
        <EmptyHint text={`视频打开失败：${video.error}`} />
      ) : video.url ? (
        <video className="learn-video-player" src={video.url} poster={poster.url ?? undefined} controls playsInline preload="metadata" data-testid="video-player" />
      ) : (
        <div className="learn-pdf-stage">
          <Spinner size={28} />
        </div>
      )}
    </div>
  );
}

// ---------- 写字 ----------

export function HandwritingPreview({ content }: { content: Content }) {
  const p = payloadOf(content);
  const data = useHanziData();
  const items = (Array.isArray(p.characters) ? p.characters : []).filter(isObject).map((x) => {
    const ch = Array.from(str(x.char) ?? "")[0] ?? "";
    const lex = data?.lexicon[ch];
    const pinyin = str(x.pinyin) || lex?.pinyin || "";
    const words = Array.isArray(x.words) && x.words.length ? x.words.map((w) => str(w) ?? "").filter(Boolean) : (lex?.words ?? []);
    return { ch, pinyin, words };
  });
  return (
    <div className="learn-hw">
      <div className="learn-preview-tags">
        <Pill>{str(p.mode) === "free" ? "自己写（不显示底字）" : "描红（显示浅色底字）"}</Pill>
        <Pill>{items.length} 个字</Pill>
      </div>
      <div className="learn-hw-grid">
        {items.map((it, i) => (
          <div key={`${it.ch}-${i}`} className="learn-hw-card" data-testid="hw-card">
            <span className="learn-pinyin">{it.pinyin || "—"}</span>
            <span className="learn-tianzige">
              <HanziGlyph data={data} char={it.ch} size={64} />
            </span>
            <span className="learn-words">{it.words.length ? it.words.join("、") : "没有组词"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- H5 ----------

export function WebActivityInfo({ content }: { content: Content }) {
  const p = payloadOf(content);
  const pkg = isObject(p.package) ? p.package : {};
  const id = str(pkg.id);
  const assetId = str(pkg.asset_id) ?? "";
  const params = isObject(p.params) ? p.params : {};
  const [game, setGame] = useState<WebPackage | null>(null);
  useEffect(() => {
    if (!id) return;
    let alive = true;
    gameOf(id, str(pkg.version))
      .then((g) => alive && setGame(g))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // 参数按游戏的 params_schema 显示名称和选项文字；Schema 里没有的键原样显示
  const fields = parseFormSchema(game?.paramsSchema);
  const rows = Object.entries(params).map(([k, v]) => {
    const f = fields.find((x) => x.key === k);
    return { key: k, title: f?.title ?? k, value: displayValue(f, v) };
  });
  return (
    <div className="learn-web">
      <EmptyHint icon={Gamepad2} text="网页版不能运行互动游戏，布置后在孩子的平板上使用" />
      <FormCard>
        <ValueRow title="游戏" value={game?.name ?? id ?? "—"} />
        <RowDivider />
        <ValueRow title="版本" value={str(pkg.version) ?? "—"} />
        <RowDivider />
        <ValueRow title="来源" value={assetId.startsWith("builtin:") ? "平板内置" : "游戏库"} />
      </FormCard>
      {rows.length > 0 && (
        <FormCard>
          {rows.map((r, i) => (
            <div key={r.key}>
              {i > 0 && <RowDivider />}
              <ValueRow title={r.title} value={r.value} />
            </div>
          ))}
        </FormCard>
      )}
    </div>
  );
}

function displayValue(f: FormField | undefined, v: JSONValue): string {
  if (f?.kind.kind === "choice" && typeof v === "string") return f.kind.labels[v] ?? v;
  if (f?.kind.kind === "integer" && typeof v === "number") return `${v}${f.kind.unit ?? ""}`;
  if (typeof v === "boolean") return v ? "是" : "否";
  return typeof v === "string" ? v : JSON.stringify(v);
}
